// Pieces shared by the stock screens (Sprint 4; design/screens/stock.html, stock-take.html, stock-transfer.html; UC-INVENTORY-001..012):
// the words of the inventory's vocabularies as the screens show them (packages/contracts/src/inventory.ts — BR-INVENTORY-005's
// movement words, BR-INVENTORY-011's serial words, the stock-take and transfer chips of DESIGN.md), a person as the fixture prints
// one ("K Molefe (0006)"), the branch a stock read names (the acting branch, another the user reads, or All branches for an Owner
// or Admin, BR-INVENTORY-024), a movement in the fixture's words (UC-INVENTORY-003 step 2), the device a count names
// (BR-INVENTORY-017) and the shared page head. Nothing here invents a class: the markup is the prototype's.
import {
  STOCK_MOVEMENT_REASON_LABELS, STOCK_STATE_LABELS, STOCK_TAKE_STATUS_LABELS, STOCK_TRANSFER_STATUS_LABELS,
  type InventoryUserRef, type SerialStatus, type StockDocumentRef, type StockMovementEndpoint, type StockMovementView, type StockState, type StockTakeStatus,
  type StockTakeSummary, type StockTransferStatus,
} from "@visionx/contracts";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { transfers } from "../api/endpoints.inventory";
import { suppliers } from "../api/endpoints.purchasing";
import { decodeBarcodePhoto, touchDevice } from "../lib/barcode-camera";
import { formatDate, formatDayMonth, formatMoney, formatTime, localDate } from "../lib/format";
import { useOneStore } from "../session/one-store";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Chip, type Tone } from "../ui/bits";
import { Icon } from "../ui/icons";
import { SERIAL_WORD, andList, tabLabelOf } from "./CatalogueShared";

export const STATE_WORD: Readonly<Record<StockState, string>> = STOCK_STATE_LABELS;
export const REASON_WORD = STOCK_MOVEMENT_REASON_LABELS;

/** A signed whole number with the design system's minus (DESIGN.md): `+5`, `−1`, `0`. */
export const signed = (n: number): string => (n > 0 ? `+${n}` : n < 0 ? `−${-n}` : "0");

// --- people -----------------------------------------------------------------------------------------------------------------

/** A Control ID as the fixture prints it: `0006`. */
export const controlIdText = (n: number | null | undefined): string => (n === null || n === undefined ? "" : String(n).padStart(4, "0"));

/** "K Molefe", "Dr N Khumalo": the first name's initial and the surname, as the stock screens print a person (FIXTURE §5). */
export function shortName(displayName: string | null | undefined): string {
  const parts = (displayName ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] ?? "";
  const last = parts[parts.length - 1]!;
  const first = parts[0]!;
  return /^Dr\.?$/i.test(first) && parts.length > 2 ? `Dr ${parts[1]!.charAt(0)} ${last}` : `${first.charAt(0)} ${last}`;
}

/** "K Molefe (0006)". */
export const personWithId = (u: InventoryUserRef | null | undefined): string => (u ? `${shortName(u.displayName)}${u.controlId !== null ? ` (${controlIdText(u.controlId)})` : ""}` : "—");
/** "0003 Dr N Khumalo" — the authoriser first by Control ID, as stock-take.html prints it. */
export const idThenPerson = (u: InventoryUserRef | null | undefined): string => (u ? `${u.controlId !== null ? `${controlIdText(u.controlId)} ` : ""}${shortName(u.displayName)}` : "—");

// --- chips ------------------------------------------------------------------------------------------------------------------

const SERIAL_TONE: Readonly<Record<SerialStatus, [Tone, boolean]>> = {
  Available: ["ok", true], Reserved: ["info", true], AtLab: ["warn", true], InTransit: ["info", true], Damaged: ["crit", true],
  Sold: ["neutral", false], WrittenOff: ["crit", false], Returned: ["neutral", false],
};
/** A serial's status in words, never colour alone (an Available frame is In store, BR-INVENTORY-011). */
export function SerialStatusChip({ status }: { status: SerialStatus }) {
  const [tone, dot] = SERIAL_TONE[status];
  return <Chip tone={tone} dot={dot}>{SERIAL_WORD[status]}</Chip>;
}

const TRANSFER_TONE: Readonly<Record<StockTransferStatus, [Tone, boolean]>> = {
  Draft: ["neutral", false], Sent: ["info", true], PartiallyReceived: ["warn", true], Received: ["ok", true], Cancelled: ["neutral", false],
};
/** DESIGN.md's transfer chips — Draft · In transit · Received — with Partially received and Cancelled (UC-INVENTORY-011, open questions). */
export function TransferStatusChip({ status }: { status: StockTransferStatus }) {
  const [tone, dot] = TRANSFER_TONE[status];
  return <Chip tone={tone} dot={dot}>{STOCK_TRANSFER_STATUS_LABELS[status]}</Chip>;
}

const TAKE_TONE: Readonly<Record<StockTakeStatus, [Tone, boolean]>> = { Open: ["info", true], Posting: ["info", true], Finalised: ["ok", true], Abandoned: ["neutral", false] };
/** DESIGN.md's stock-take chips — In progress · Finalised — with Abandoned and Posting… (UC-INVENTORY-007, open questions). */
export function StockTakeStatusChip({ status }: { status: StockTakeStatus }) {
  const [tone, dot] = TAKE_TONE[status];
  return <Chip tone={tone} dot={dot}>{STOCK_TAKE_STATUS_LABELS[status]}</Chip>;
}

export function ReOrderChip() { return <Chip tone="warn">Re-order</Chip>; }

// --- dates ------------------------------------------------------------------------------------------------------------------

/** Whether an instant falls on today's date in the organisation's calendar. */
export const isToday = (iso: string): boolean => localDate(new Date(iso)) === localDate(new Date());

/** "30–31 May 2026" for a count that ran over two days, "28 Aug 2026" for one. */
export function dayRange(fromIso: string, toIso: string | null | undefined): string {
  if (!toIso) return formatDate(fromIso);
  const a = new Date(fromIso);
  const b = new Date(toIso);
  if (localDate(a) === localDate(b)) return formatDate(b);
  const [ya, ma] = localDate(a).split("-");
  const [yb, mb] = localDate(b).split("-");
  if (ya === yb && ma === mb) return `${localDate(a).slice(8).replace(/^0/, "")}–${formatDate(b)}`;
  return `${formatDayMonth(a)} – ${formatDate(b)}`;
}

/** "30–31 May" (no year) for the stats strip. */
export function dayRangeShort(fromIso: string, toIso: string | null | undefined): string {
  if (!toIso || localDate(new Date(fromIso)) === localDate(new Date(toIso))) return formatDayMonth(toIso ?? fromIso);
  const a = localDate(new Date(fromIso));
  const b = localDate(new Date(toIso));
  if (a.slice(0, 7) === b.slice(0, 7)) return `${a.slice(8).replace(/^0/, "")}–${formatDayMonth(toIso)}`;
  return `${formatDayMonth(fromIso)} – ${formatDayMonth(toIso)}`;
}

// --- the branch a stock read names (BR-INVENTORY-024) -----------------------------------------------------------------------

/** BR-INVENTORY-024: an Owner or Admin may read every branch at once (`all`), as BR-APPOINTMENTS-012 has it. */
export const readsAllBranches = (role: string | undefined): boolean => role === "Owner" || role === "Admin";

export interface StockBranch {
  /** The branch the reads name: the acting branch, another the user reads, or `all`. */
  branchId: string | null;
  isAll: boolean;
  /** Writes are made at the acting branch (BR-INVENTORY-024): only while it is the branch in view. */
  isActing: boolean;
  actingId: string | null;
  actingName: string | null;
  name: string;
  choices: { id: string; name: string }[];
  choose: (id: string) => void;
}

/**
 * The branch picker's branch, kept in the address as `?branch=`; the acting branch when none is named. At a practice of one store
 * there is nothing to pick — no All branches, to the Owner and an Admin too, and `?branch=all` reads the store (UC-INVENTORY-001
 * A8; DVN's answers of 28 Oct 2026, change 13) — so every picker built on this one choice (Stock, the movements, the adjustments,
 * the goods receipts) is left out.
 */
export function useStockBranch(): StockBranch {
  const s = useSessionView();
  const [sp, setSp] = useSearchParams();
  const oneStore = useOneStore();
  const all = readsAllBranches(s?.user.role) && !oneStore;
  const acting = s?.activeBranchId ?? null;
  const readable = s?.switchableBranches ?? [];
  const raw = sp.get("branch");
  const valid = raw === "all" ? all : raw !== null && readable.some((b) => b.id === raw);
  const branchId = valid ? raw : acting;
  const nameOf = (id: string | null): string => (id === "all" ? "All branches" : readable.find((b) => b.id === id)?.name ?? s?.assignments.find((a) => a.branchId === id)?.branchName ?? "");
  return {
    branchId, isAll: branchId === "all", isActing: branchId !== null && branchId === acting, actingId: acting, actingName: acting ? nameOf(acting) : null, name: nameOf(branchId),
    choices: [...readable.map((b) => ({ id: b.id as string, name: b.name })), ...(all ? [{ id: "all", name: "All branches" }] : [])],
    choose: (id) => setSp((p) => { const n = new URLSearchParams(p); if (id === acting) n.delete("branch"); else n.set("branch", id); n.delete("product"); n.delete("at"); return n; }, { replace: true }),
  };
}

/**
 * Whether Stock offers Transfer (UC-INVENTORY-011 A12, -012 A7): always at a practice of several branches; at a practice of one
 * store, where there is nowhere to send stock, only while the store has a transfer — out or in, its newest of each read — so a
 * draft or a sent transfer from before, or one a branch closed since sent to it, can still be cancelled or received. Until the two
 * reads answer nothing is offered there; should one fail, Transfer is offered, losing nothing.
 */
export function useTransferOffered(): boolean {
  const s = useSessionView();
  const oneStore = useOneStore();
  const acting = s?.activeBranchId ?? null;
  const allowed = can(s, "inventory.transfer.write");
  const ask = (direction: "outbound" | "inbound") => ({
    queryKey: ["transfers", "any", direction, acting], queryFn: () => transfers.list({ direction, branchId: acting ?? undefined }, { limit: 1 }),
    enabled: allowed && oneStore && acting !== null, staleTime: 30_000,
  });
  const out = useQuery(ask("outbound"));
  const into = useQuery(ask("inbound"));
  if (!allowed) return false;
  if (!oneStore) return true;
  return out.isError || into.isError || (out.data?.data.items.length ?? 0) > 0 || (into.data?.data.items.length ?? 0) > 0;
}

/** The acting branch's name and id, for the screens that write there. */
export function useActingBranch(): { id: string | null; name: string; code: string } {
  const s = useSessionView();
  const id = s?.activeBranchId ?? null;
  const b = s?.switchableBranches.find((x) => x.id === id);
  const a = s?.assignments.find((x) => x.branchId === id);
  return { id, name: b?.name ?? a?.branchName ?? "", code: b?.code ?? a?.branchCode ?? "" };
}

/** Why a stock write is off right now — offline, a suspended organisation (D13, D28) — with the shell's sentence. */
export function useStockWrites(): { online: boolean; title: string | undefined } {
  const block = useWriteBlock();
  return { online: block === null, title: block ? WRITE_BLOCK_TITLE[block] : undefined };
}

// --- the device a count names (BR-INVENTORY-017) ----------------------------------------------------------------------------

const DEVICE_KEY = "vx.stock.device";
/** The name this browser gives itself on a count's entries — "Front desk PC 1", "Consulting-room iPad" — kept on this device only. */
export function readDeviceName(): string {
  try { return localStorage.getItem(DEVICE_KEY) ?? ""; } catch { return ""; }
}
export function writeDeviceName(name: string): void {
  try { if (name.trim()) localStorage.setItem(DEVICE_KEY, name.trim()); else localStorage.removeItem(DEVICE_KEY); } catch { /* storage unavailable */ }
}

// --- a tablet's camera as a scanner (CAT-62) --------------------------------------------------------------------------------

/** What a photo with no code read, or a browser with no barcode reader, is told: the words the count screen first used. */
export const CAMERA_NOTHING_READ = "No barcode was read. Hold the code flat and close to the camera, or type it.";
const cameraUnreadable = (err: unknown) => `This device cannot read barcodes from the camera. ${err instanceof Error ? err.message : ""} Type the code or use a scanner.`;

/**
 * "Scan with camera" on a touch screen (UC-CATALOGUE-004 A8; UC-INVENTORY-008, -006, -011, -012, UC-PURCHASING-005): a photo from
 * the rear camera, read on the device by the browser's barcode detector into the code a USB scanner would type, handed to the
 * screen's own scan (`onCode`); a photo with no code, or a browser with no detector, is said (`onMiss`) and nothing is sent. A desk
 * computer, which has no such camera, is offered nothing. On the count screen, and since the completeness audit of 23 Oct 2026 on
 * Receive stock, the Adjust dialog and a transfer's draft and receive screens (TC-INVENTORY-171, -173, TC-PURCHASING-129).
 */
export function CameraScan({ testId, disabled, title, onCode, onMiss }: {
  testId: string; disabled?: boolean | undefined; title?: string | undefined; onCode: (code: string) => void; onMiss: (words: string) => void;
}) {
  const [touch] = useState(touchDevice);
  const input = useRef<HTMLInputElement>(null);
  if (!touch) return null;
  const read = async (file: File | undefined) => {
    if (input.current) input.current.value = "";
    if (!file) return;
    try {
      const code = await decodeBarcodePhoto(file);
      if (code) onCode(code); else onMiss(CAMERA_NOTHING_READ);
    } catch (err) { onMiss(cameraUnreadable(err)); }
  };
  return (
    <>
      <input ref={input} type="file" accept="image/*" capture="environment" hidden aria-hidden="true" tabIndex={-1} data-testid={testId} onChange={(e) => void read(e.target.files?.[0])} />
      <button className="btn" type="button" disabled={disabled} title={title} onClick={() => input.current?.click()}><Icon name="camera" />Scan with camera</button>
    </>
  );
}

// --- documents --------------------------------------------------------------------------------------------------------------

/**
 * A document a movement or a hold names, linked when its screen is built (adjustments, stock takes, transfers, goods receipts) and
 * the user may open it; else its number as text. Each screen's own code decides: an adjustment `inventory.adjustment.write`, a
 * stock take `inventory.stock_take.read`, a transfer `inventory.transfer.write` (the stock roles, W6), a goods receipt
 * `purchasing.po.read` (UC-PURCHASING-008 A3, E2) — so a Receptionist reads the numbers and is never sent to a page that refuses
 * her (the review of 18 Oct 2026, D1-12; TC-INVENTORY-150).
 */
export function DocumentRef({ doc }: { doc: StockDocumentRef | null | undefined }) {
  const s = useSessionView();
  if (!doc?.number) return null;
  const to = doc.id === null ? null
    : doc.type === "StockAdjustment" ? (can(s, "inventory.adjustment.write") ? `/stock/adjustments/${doc.id}` : null)
      : doc.type === "StockTake" ? (can(s, "inventory.stock_take.read") ? `/stock-take/${doc.id}` : null)
        : doc.type === "StockTransfer" ? (can(s, "inventory.transfer.write") ? `/stock-transfer/${doc.id}` : null)
          : doc.type === "GoodsReceipt" && can(s, "purchasing.po.read") ? `/purchasing/receipts/${doc.id}` : null;
  return to ? <Link className="mono" to={to}>{doc.number}</Link> : <span className="mono">{doc.number}</span>;
}

/** The Control log, linked for the roles that read it (an Owner or Admin, a Branch Owner for their branch); else the words. */
export function ControlLogRef() {
  const s = useSessionView();
  return can(s, "governance.control_log.read") ? <Link to="/control-log">Control log</Link> : <>Control log</>;
}

/** The supplier's name, read once and kept (a stock-take scope names suppliers by id). */
export function SupplierName({ id }: { id: string }) {
  const q = useQuery({ queryKey: ["supplier", id], queryFn: () => suppliers.get(id), staleTime: 5 * 60_000 });
  return <>{q.data?.data.name ?? "a supplier"}</>;
}

/** A count's scope in words: "Contact lenses · cycle count", "All categories · opening stock take" (stock-take.html's History). */
export function scopeWords(t: Pick<StockTakeSummary, "kind" | "scope">): ReactNode {
  if (t.kind === "Opening") return "All categories · opening stock take";
  const sc = t.scope;
  const cats = [...new Set((sc?.categories ?? []).map((c) => tabLabelOf(c as never)))];
  const parts: ReactNode[] = [cats.length ? andList(cats) : "All categories"];
  if (sc?.brands?.length) parts.push(andList(sc.brands));
  if (sc?.supplierIds?.length) parts.push(<>from {sc.supplierIds.map((id, i) => <Fragment key={id}>{i > 0 ? ", " : ""}<SupplierName id={id} /></Fragment>)}</>);
  const words = t.kind === "Full" && !sc ? "full count" : "cycle count";
  return <>{parts.map((p, i) => <Fragment key={i}>{i > 0 ? " · " : ""}{p}</Fragment>)} · {words}</>;
}

// --- movements in words (UC-INVENTORY-003 step 2) ---------------------------------------------------------------------------

const STATES: readonly StockMovementEndpoint[] = ["InStore", "Reserved", "AtLab", "Damaged", "InTransit"];
const isState = (e: StockMovementEndpoint): e is StockState => STATES.includes(e);

/** The rows of one document posted together — PO-MEN-000226's five receipts — read as one entry, as stock.html draws them. */
export function groupMovements(rows: readonly StockMovementView[]): StockMovementView[][] {
  const out: StockMovementView[][] = [];
  for (const m of rows) {
    const last = out[out.length - 1];
    const head = last?.[0];
    const same = head && m.document?.number && head.document?.number === m.document.number && head.type === m.type && head.branchId === m.branchId
      && head.occurredAt.slice(0, 16) === m.occurredAt.slice(0, 16) && head.user?.id === m.user?.id && head.fromState === m.fromState && head.toState === m.toState;
    if (same) last!.push(m); else out.push([m]);
  }
  return out;
}

/** "2026-09-10-001–005" for a run of one day's serials; else the serials one after another. */
export function serialRun(numbers: readonly string[]): string {
  const sorted = [...numbers].sort();
  if (sorted.length <= 1) return sorted[0] ?? "";
  const m = sorted.map((s) => /^(\d{4}-\d{2}-\d{2}-)(\d{3,})$/.exec(s));
  if (m.every((x) => x !== null && x[1] === m[0]![1])) {
    const nums = m.map((x) => Number(x![2]));
    if (nums.every((n, i) => i === 0 || n === nums[i - 1]! + 1)) return `${sorted[0]}–${m[m.length - 1]![2]}`;
  }
  return sorted.join(", ");
}

/** One entry of the Movements panel: the time, what happened and its sub-line, in the fixture's words. */
export function MovementItem({ group, showBranch }: { group: StockMovementView[]; showBranch?: boolean | undefined }) {
  const m = group[0]!;
  const delta = group.reduce((n, x) => n + x.onHandDelta, 0);
  const units = group.reduce((n, x) => n + x.quantity, 0);
  const today = isToday(m.occurredAt);
  const products = [...new Map(group.map((x) => [x.productId, x])).values()];
  const serialNumbers = group.map((x) => x.serialNumber).filter((x): x is string => !!x);
  const between = isState(m.fromState) && isState(m.toState);
  const total = group.reduce((n, x) => n + (x.totalCostCents ?? 0), 0);
  const cost = group.length === 1 && m.unitCostCents !== null
    ? (m.quantity === 1 ? `at ${formatMoney(m.unitCostCents)}` : `${m.quantity} at ${formatMoney(m.unitCostCents)} · ${formatMoney(m.totalCostCents)}`)
    : group.some((x) => x.totalCostCents !== null) ? `${formatMoney(total)} at cost` : null;
  const what = products.length === 1
    ? <>{m.productName}{serialNumbers.length > 0 && <> {serialNumbers.length > 1 ? "serials " : ""}<span className="mono">{serialRun(serialNumbers)}</span></>}</>
    : <>{products.map((p) => `${p.productName} ×${group.filter((x) => x.productId === p.productId).reduce((n, x) => n + x.quantity, 0)}`).join(", ")}{serialNumbers.length > 0 && <> · serials <span className="mono">{serialRun(serialNumbers)}</span></>}</>;
  const sub: ReactNode[] = [
    today ? "Today" : formatTime(m.occurredAt),
    ...(showBranch ? [m.branchName] : []),
    ...(between ? [`${STATE_WORD[m.fromState as StockState]} → ${STATE_WORD[m.toState as StockState]} · on hand unchanged`] : []),
    what,
    ...(cost ? [cost] : []),
    ...(m.user ? [m.authorisedBy ? personWithId(m.user) : shortName(m.user.displayName)] : []),
    ...(m.authorisedBy ? [<>authorised by {personWithId(m.authorisedBy)}</>] : []),
    ...(m.controlLogId ? [<ControlLogRef />] : []),
  ];
  return (
    <div className="item">
      <span className="t">{today ? formatTime(m.occurredAt) : formatDayMonth(m.occurredAt)}</span>
      <div className="grow">
        <div className="primary">
          {m.typeLabel} · {delta !== 0 ? signed(delta) : units}
          {m.document?.number && <> · <DocumentRef doc={m.document} /></>}
          {m.note && (m.type === "WriteOff" || m.type === "Adjustment") ? ` · ${m.note}` : m.reason && (m.type === "WriteOff" || m.type === "Adjustment" || m.type === "StockTake") ? ` · ${REASON_WORD[m.reason]}` : ""}
        </div>
        <div className="sub">{sub.map((p, i) => <Fragment key={i}>{i > 0 ? " · " : ""}{p}</Fragment>)}</div>
      </div>
    </div>
  );
}
